import { useRef } from "react"
import type { TrackReferenceOrPlaceholder } from "@livekit/components-react"

import { AnnotationCanvas } from "./annotations/AnnotationCanvas"
import { useAnnotationSession } from "./annotations/useAnnotationSession"
import { useFreezeController } from "./freeze/useFreezeController"
import { SyncStageOverlay } from "./stage/SyncStageOverlay"
import { SyncToolbar } from "./SyncToolbar"

/**
 * StageSyncLayer - the one composite mounted inside Phase 4's `div.rs-stage` (via
 * EquipmentStage's renderStageOverlay), only ever under a RoomSyncProvider. Wires this stage's
 * overlay (pointer + frozen frame canvas), the freeze controller, the annotation session and the
 * toolbar together so a single caller gets pointer, freeze, annotations and the toolbar for free
 * (RS-09 through RS-15).
 *
 * The wrapper is `display: contents` so it never becomes a positioned ancestor of its own: the
 * overlay's `absolute inset-0` still resolves against `div.rs-stage` itself, and `getStageVideoElement`
 * can walk from this element's `parentElement` (the stage) to the stage's own `<video>`, exactly
 * like SyncStageOverlay's own measurement does.
 */
export function StageSyncLayer(p: {
  stageTrack: TrackReferenceOrPlaceholder | undefined
  layout: "desktop" | "mobile"
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const freeze = useFreezeController({
    getStageVideoElement: () => wrapperRef.current?.parentElement?.querySelector("video") ?? null,
  })
  const session = useAnnotationSession()

  const interaction: "pointer" | "draw" | "none" =
    session.canDraw && session.tool !== "pointer" ? "draw" : "pointer"

  return (
    <div ref={wrapperRef} className="cl:contents">
      <SyncStageOverlay interaction={interaction} stageTrack={p.stageTrack}>
        {(surface) =>
          surface.frozen ? (
            <AnnotationCanvas
              canModify={session.canModify}
              color={session.color}
              interactive={session.canDraw}
              selectedShapeId={session.selectedShapeId}
              shapes={session.shapes}
              surface={surface}
              tool={session.tool}
              width={session.width}
              onCreate={session.create}
              onMove={session.move}
              onSelect={session.setSelectedShapeId}
            />
          ) : null
        }
      </SyncStageOverlay>
      <SyncToolbar freeze={freeze} layout={p.layout} session={session} stageTrack={p.stageTrack} />
    </div>
  )
}

export default StageSyncLayer
